var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var _a;
import { jsx, jsxs } from "react/jsx-runtime";
import React__default, { Component } from "react";
import filter from "lodash/filter";
import isEmpty from "lodash/isEmpty";
import minBy from "lodash/minBy";
import PropTypes from "prop-types";
import { withTheme } from "styled-components";
import { line } from "d3-shape";
import { extent } from "d3-array";
import { scaleOrdinal, schemeCategory20c, scaleTime, scaleLinear } from "d3-scale";
import MousePositionHOC from "../Chart/common/MousePosition.esm.js";
import Chart from "../Chart/Chart.esm.js";
import XAxis from "../Chart/XAxis.esm.js";
import YAxis from "../Chart/YAxis.esm.js";
import Line from "./Line.esm.js";
import DataSeries from "./DataSeries.esm.js";
import Legend from "./Legend.esm.js";
import { ChartInfoPoints, ChartInfoPointsLegend } from "./ChartInfoPoints.esm.js";
import ChartDescription from "./ChartDescription.esm.js";
import { handleFieldChangeEvent } from "../../AmortizationCalculator/AmortizationCalculator.Clickstream.esm.js";
function flattenData(data, xAccessor, yAccessor) {
  var allValues = [];
  var xValues = [];
  var yValues = [];
  var coincidentCoordinateCheck = {};
  data.forEach((series, i) => {
    series.values.forEach((item, j) => {
      var x = xAccessor(item);
      if (isNaN(x)) {
        return;
      }
      xValues.push(x);
      var y = yAccessor(item);
      var yNode;
      if (typeof y === "object" && Object.keys(y).length > 0) {
        Object.keys(y).forEach((key) => {
          if (isNaN(y[key])) {
            return;
          }
          yValues.push(y[key]);
          yNode = 0;
        });
      } else {
        if (isNaN(y)) {
          return;
        }
        yValues.push(y);
        yNode = y;
      }
      var xyCoords = `${x}-${yNode}`;
      if (coincidentCoordinateCheck.hasOwnProperty(xyCoords)) {
        return;
      }
      coincidentCoordinateCheck[xyCoords] = "";
      var pointItem = {
        coord: {
          x,
          y: yNode
        },
        d: item,
        id: series.name + j,
        series,
        seriesIndex: i
      };
      allValues.push(pointItem);
    });
  });
  return {
    allValues,
    xValues,
    yValues
  };
}
function calculateScales(chartWidth, chartHeight, xValues, yValues) {
  let xScale, yScale;
  if (xValues.length > 0 && Object.prototype.toString.call(xValues[0]) === "[object Date]") {
    xScale = scaleTime().range([0, chartWidth]);
  } else {
    xScale = scaleLinear().range([0, chartWidth]);
  }
  xScale.domain(extent(xValues));
  if (yValues.length > 0 && Object.prototype.toString.call(yValues[0]) === "[object Date]") {
    yScale = scaleTime().range([chartHeight, 0]);
  } else {
    yScale = scaleLinear().range([chartHeight, 0]);
  }
  yScale.domain(extent(yValues));
  return {
    xScale,
    yScale
  };
}
const LineChart = withTheme((_a = class extends Component {
  constructor() {
    super(...arguments);
    __publicField(this, "_nearest", (data, val, xA) => {
      if (data && data.length) {
        var i = 0, diff = Math.abs(val - xA(data[i])), iDiff;
        for (i = 1; i < data.length; i += 1) {
          iDiff = Math.abs(val - xA(data[i]));
          if (iDiff > diff) {
            break;
          }
          diff = iDiff;
        }
        return i - 1;
      }
      return null;
    });
  }
  render() {
    var _a2, _b, _c;
    const {
      data,
      interpolationType = this.interpolate ? "cardinal" : "linear",
      containerWidth,
      margins,
      height,
      legend,
      legendOffset,
      xAccessor,
      yAccessor,
      mouseX,
      mouseY,
      colors,
      lineWidth,
      title,
      xAxisTickCount,
      pointRadius,
      displayDataPoints,
      legendIndexFormatter,
      yAxisTickCount,
      yAxisFormatter,
      yHideOrigin,
      axesColor = this.props.theme.constellation.colors.black,
      tickTextStroke,
      xAxisFormatter,
      xHideOrigin,
      theme,
      xLabel,
      yLabel,
      fontFamily,
      interactionEventCmsInfo
    } = this.props;
    var values = data && data[0] && data[0].values;
    var chartWidth, chartHeight;
    chartWidth = containerWidth - margins.left - margins.right;
    chartHeight = height - margins.top - margins.bottom;
    if (legend) {
      chartWidth = chartWidth - legendOffset;
    }
    let dataArray = data;
    if (!Array.isArray(data)) {
      dataArray = [data];
    }
    var flattenedData = flattenData(dataArray, xAccessor, yAccessor);
    var xValues = flattenedData.xValues, yValues = flattenedData.yValues;
    var scales = calculateScales(chartWidth, chartHeight, xValues, yValues);
    var mouseOverLine = null;
    var xDomain = scales.xScale.range(), yDomain = scales.yScale.range();
    let mouseMoveIsInDomain = true;
    if (mouseX - 20 < xDomain[0] || mouseX - 20 > xDomain[1] || mouseY - 10 < yDomain[1] || mouseY - 10 > yDomain[0]) {
      mouseMoveIsInDomain = false;
    }
    var hoverIndex = null;
    if (mouseMoveIsInDomain) {
      var fnLine = line().x((d) => d[0]).y((d) => d[1]);
      hoverIndex = this._nearest(dataArray[0].values, scales.xScale.invert(mouseX - 20), xAccessor);
      mouseOverLine = /* @__PURE__ */ jsx(Line, { path: fnLine([[mouseX - 20, scales.yScale.range()[0]], [mouseX - 20, scales.yScale.range()[1]]]), strokeWidth: null, stroke: theme.constellation.colors.borderDark });
      if (interactionEventCmsInfo) {
        handleFieldChangeEvent(interactionEventCmsInfo.label, interactionEventCmsInfo.block_type);
      }
    }
    const infoPoints = filter(values, (v) => v.info);
    var activeInfoPoint = null;
    infoPoints.forEach((p) => {
      p.active = false;
    });
    if (hoverIndex && infoPoints && !isEmpty(infoPoints)) {
      activeInfoPoint = minBy(infoPoints, (point) => Math.abs(point.x - hoverIndex));
      activeInfoPoint.active = true;
    }
    var DataSeries$1 = DataSeries;
    var trans = `translate(${margins.left}, ${margins.top})`;
    var dataSeriesArray = dataArray.map((series, idx) => /* @__PURE__ */ jsx(DataSeries$1, { xScale: scales.xScale, yScale: scales.yScale, mouseX: this.mouseX - 20, hoverIndex, seriesName: series.name, data: series.values, width: chartWidth, height: chartHeight, fill: colors(idx) || theme.constellation.colors.black, strokeWidth: lineWidth, pointRadius, xAccessor: series.xAccessor || xAccessor, yAccessor: series.yAccessor || yAccessor, interpolationType, displayDataPoints, children: /* @__PURE__ */ jsx(ChartInfoPoints, { infoPoints, xScale: scales.xScale, yScale: scales.yScale }) }, series.name));
    const name = (_a2 = dataArray[0]) == null ? void 0 : _a2.name;
    const yFormatter = (_b = dataArray[0]) == null ? void 0 : _b.formatter;
    const allDataPoints = ((_c = dataArray[0]) == null ? void 0 : _c.values) ?? [];
    return /* @__PURE__ */ jsxs(React__default.Fragment, { children: [
      /* @__PURE__ */ jsx(Legend, { data: dataArray, hoverIndex, indexFormatter: legendIndexFormatter, yAccessor, colors, fontFamily }),
      /* @__PURE__ */ jsx(ChartInfoPointsLegend, { activeInfoPoint, hoverIndex }),
      /* @__PURE__ */ jsx(ChartDescription, { name, xLabel: xLabel ?? "", yLabel: yLabel ?? name, xFormatter: legendIndexFormatter, yFormatter, dataPoints: allDataPoints }),
      /* @__PURE__ */ jsx(Chart, { fontFamily, legend, data: dataArray, margins, colors, width: containerWidth, height, title, children: /* @__PURE__ */ jsxs("g", { transform: trans, className: "rd3-linechart", children: [
        /* @__PURE__ */ jsx(YAxis, { yAxisClassName: "rd3-linechart-yaxis", yScale: scales.yScale, yAxisTickCount, yHideOrigin, tickStroke: axesColor || theme.constellation.colors.black, tickTextStroke, tickSize: -chartWidth, yOrient: "right", tickFormatting: yAxisFormatter, margins, width: chartWidth, height: chartHeight, stroke: axesColor || theme.constellation.colors.black, strokeWidth: "0px" }),
        /* @__PURE__ */ jsx(XAxis, { xAxisClassName: "rd3-linechart-xaxis", xScale: scales.xScale, xAxisTickCount, tickFormatting: xAxisFormatter, tickSize: 0, xHideOrigin, xOrient: "bottom", margins, width: chartWidth, height: chartHeight, stroke: axesColor, tickStroke: axesColor, tickTextStroke, strokeWidth: "2px" }),
        mouseOverLine,
        dataSeriesArray
      ] }) })
    ] });
  }
}, __publicField(_a, "propTypes", {
  axesColor: PropTypes.string,
  axesStrokeWidth: PropTypes.number,
  colors: PropTypes.func,
  displayDataPoints: PropTypes.bool,
  height: PropTypes.number,
  legend: PropTypes.bool,
  legendOffset: PropTypes.number,
  margins: PropTypes.object,
  mouseX: PropTypes.number,
  mouseY: PropTypes.number,
  pointRadius: PropTypes.number,
  title: PropTypes.string,
  width: PropTypes.number,
  xAccessor: PropTypes.func,
  xHideOrigin: PropTypes.bool,
  yAccessor: PropTypes.func,
  yHideOrigin: PropTypes.bool,
  containerWidth: PropTypes.number,
  interpolate: PropTypes.bool,
  data: PropTypes.array,
  interpolationType: PropTypes.string,
  lineWidth: PropTypes.number,
  xAxisTickCount: PropTypes.number,
  legendIndexFormatter: PropTypes.func,
  xAxisFormatter: PropTypes.func,
  tickTextStroke: PropTypes.string,
  yAxisFormatter: PropTypes.func,
  yAxisTickCount: PropTypes.number,
  theme: PropTypes.object,
  xLabel: PropTypes.string,
  yLabel: PropTypes.string,
  fontFamily: PropTypes.string,
  interactionEventCmsInfo: PropTypes.object
}), __publicField(_a, "defaultProps", {
  colors: scaleOrdinal(schemeCategory20c),
  displayDataPoints: true,
  height: 200,
  interpolate: false,
  legendOffset: 120,
  margins: {
    top: 10,
    right: 20,
    bottom: 40,
    left: 30
  },
  pointRadius: 3,
  title: "",
  width: 400,
  xAccessor: (d) => d.x,
  yAccessor: (d) => d.y
}), _a));
const LineChart$1 = MousePositionHOC()(LineChart);
export {
  LineChart$1 as default
};
